import React, { ReactElement, useContext, useEffect, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { formatSizePercent } from "helpers/app";

import { isNil } from "ramda";

import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import IconSelector from "components/shared/images/IconSelector";
import SemiCircularProgress from "components/shared/progressBar/SemiCircularProgress";
import GenericStatus from "components/shared/status/GenericStatus";

import {
 editSiteDetails,
 getAllIspNotPaged,
 updateSiteStatus
} from "redux/handlers/websitesHandler";

import { getIspList } from "redux/selectors/ispSelector";
import { getWebsitesDetails } from "redux/selectors/websitesSelector";

import { useAppDispatch } from "hooks/reduxHook";

import { AppContext } from "../../../AppContext";
import UpdateCertificate from "../actions/UpdateCertificate";

const WebsiteRecap = (): ReactElement => {
 const { socket } = useContext(AppContext);
 const dispatch = useAppDispatch();
 const intl = useIntl();
 const theme = useTheme();
 const navigate = useNavigate();

 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));
 const websitesDetails = useSelector(getWebsitesDetails);
 const ispList = useSelector(getIspList);

 const [trafficLogs, setTrafficLogs] = useState<number>(-1);
 const [logSize, setLogSize] = useState<number>(-1);
 const [size, setSize] = useState<number>(-1);
 const [loading, setLoading] = useState<boolean>(true);

 useEffect(() => {
  (async () => {
   await dispatch(getAllIspNotPaged("webserver"));
   setLoading(false);
  })();
 }, []);

 useEffect(() => {
  !isNil(socket) &&
   socket.on("site_logs", async (value): Promise<void> => {
    for (let element of value) {
     if (element.site === websitesDetails.site) {
      setTrafficLogs(element.website_traffic ? parseFloat(element.website_traffic) : -1);
      setSize(
       element.website_size ? parseSizing(parseInt(element.website_size), element.website_size) : -1
      );
      setLogSize(element.website_log_size ? parseInt(element.website_log_size) : -1);
     }
    }
   });
  return () => {
   !isNil(socket) && socket.off("site_logs");
  };
 }, [socket, websitesDetails]);

 const parseSizing = (value: number, type: string): number => {
  if (type && type.includes("M")) {
   return value;
  } else if (type && type.includes("G")) {
   return value * 1024;
  } else {
   return value / 1024;
  }
 };

 const handleSwitchHttpOrLts = async (action: string, ispId: number, domain_id: number) => {
  await dispatch(editSiteDetails(action, ispId, domain_id));
 };

 const handleUpdateSiteStatus = async (domainId: number, ispId: number) => {
  await dispatch(updateSiteStatus(domainId, ispId));
  navigate("/app/sites");
 };

 return (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack justifyContent="space-between" direction={desktopViewPort ? "row" : "column"}>
    <Stack>
     <Stack spacing={1}>
      <Typography
       fontWeight="bold"
       variant="unboundedTitle"
       fontSize={16}
       textAlign="left"
       textTransform="uppercase">
       {websitesDetails.site}
      </Typography>
      {!loading && (
       <Tooltip title={intl.formatMessage({ id: "sites.details.goToServer" })}>
        <NavLink
         to={`/servers/manage/${ispList.find((element) => element.id === websitesDetails.isp_id)
          ?.server_id}/specification`}
         style={{
          textDecoration: "none"
         }}>
         <Stack direction="row" spacing={1} sx={{ cursor: "pointer" }}>
          <IconSelector icon="ServerIcon" props={{ fontSize: "small", color: "kxOrange" }} />
          <Typography variant="subtitle2" color="kxOrange">
           {ispList.find((element) => element.id === websitesDetails.isp_id)?.hostname}
          </Typography>
         </Stack>
        </NavLink>
       </Tooltip>
      )}
     </Stack>
    </Stack>
   </Stack>
   <Stack textAlign="left" mt={2} spacing={1}>
    <Stack direction="row">
     <Typography variant="subtitle2" fontWeight="bold" minWidth={180}>
      SSL
     </Typography>
     <GenericStatus status={websitesDetails.ssl === "y" ? "online" : "offline"} />
    </Stack>
    <Stack direction="row">
     <Typography variant="subtitle2" fontWeight="bold" minWidth={180}>
      Redirect HTTP - HTTPS
     </Typography>
     <GenericStatus status={websitesDetails.rewrite_to_https === "y" ? "online" : "offline"} />
    </Stack>
    {trafficLogs !== -1 && (
     <Stack direction="row">
      <Typography variant="subtitle2" fontWeight="bold" minWidth={180}>
       <FormattedMessage id="server.manage.outgoingTraffic" />
      </Typography>
      <Stack direction="row">
       <Typography variant="subtitle2">
        <FormattedNumber value={trafficLogs} unit="megabyte" />
       </Typography>
       <Typography
        sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}
        variant="caption"
        textTransform="uppercase">
        <FormattedMessage id="server.manage.mb" />
       </Typography>
      </Stack>
     </Stack>
    )}
   </Stack>
   <Stack direction="row" alignItems="center" justifyContent="space-around" mt={2}>
    <Stack direction="row" alignItems="center" justifyContent="center">
     {size !== -1 ? (
      websitesDetails.hd_quota !== -1 ? (
       <Stack alignItems="center">
        <SemiCircularProgress
         value={formatSizePercent(size, websitesDetails.hd_quota)}
         maxValue={100}
         size={80}
         thickness={8}
         percent={true}
        />
        <Typography variant="extraSmall" mt={0.5} textTransform="uppercase" fontWeight="bold">
         <FormattedMessage id="server.disk" />
         <FormattedMessage id="server.gigabytes.short" />
        </Typography>
       </Stack>
      ) : (
       <Typography
        sx={{ ml: 2 }}
        variant="extraSmall"
        fontWeight="bold"
        textTransform="uppercase"
        fontSize={13}>
        <FormattedMessage id="sites.ssd" />:{` ${size} KB`}
       </Typography>
      )
     ) : (
      <Stack>
       <Typography variant="caption" fontStyle="italic">
        SSD stats unavailable!
       </Typography>
      </Stack>
     )}
    </Stack>
    <Stack direction="row" alignItems="center" justifyContent="center">
     {logSize !== -1 ? (
      websitesDetails.traffic_quota !== -1 ? (
       <Stack alignItems="center">
        <SemiCircularProgress
         value={formatSizePercent(logSize, websitesDetails.traffic_quota * 1024) || 0}
         maxValue={100}
         size={80}
         thickness={8}
         percent={true}
        />
        <Typography variant="extraSmall" mt={0.5} textTransform="uppercase" fontWeight="bold">
         <FormattedMessage id="sites.logs" />
        </Typography>
       </Stack>
      ) : (
       <Typography
        sx={{ ml: 2 }}
        variant="extraSmall"
        fontWeight="bold"
        textTransform="uppercase"
        fontSize={13}>
        <FormattedMessage id="sites.logs" />:{` ${logSize} KB`}
       </Typography>
      )
     ) : (
      <Stack>
       <Typography variant="caption" fontStyle="italic">
        Logs stats unavailable!
       </Typography>
      </Stack>
     )}
    </Stack>
   </Stack>
   <Divider sx={{ my: 1 }} />
   <Stack direction="row" justifyContent="space-around">
    <UpdateCertificate />
    <Tooltip
     title={
      <FormattedMessage
       id={
        websitesDetails.ssl === "y" && websitesDetails.ssl_letsencrypt === "y"
         ? "sites.deactivateSsl"
         : "sites.activateSsl"
       }
      />
     }>
     <IconButton
      onClick={() =>
       handleSwitchHttpOrLts("encrypt", websitesDetails.isp_id || 0, websitesDetails.domain_id || 0)
      }>
      <IconSelector icon="LetsEncryptIcon" props={{ color: "kxOrange" }} />
     </IconButton>
    </Tooltip>
    <Tooltip
     title={
      <FormattedMessage
       id={
        websitesDetails.rewrite_to_https === "y"
         ? "sites.deactivateRedirect"
         : "sites.activateRedirect"
       }
      />
     }>
     <IconButton
      onClick={() =>
       handleSwitchHttpOrLts(
        "httpstoggle",
        websitesDetails.isp_id || 0,
        websitesDetails.domain_id || 0
       )
      }>
      {websitesDetails.rewrite_to_https === "y" ? (
       <IconSelector icon="LockedIcon" props={{ color: "kxOrange" }} />
      ) : (
       <IconSelector icon="UnlockedIcon" props={{ color: "kxOrange" }} />
      )}
     </IconButton>
    </Tooltip>
    <IconButton
     onClick={() =>
      handleUpdateSiteStatus(websitesDetails.domain_id || 0, websitesDetails.isp_id || 0)
     }>
     <IconSelector icon="PowerIcon" props={{ color: "kxOrange" }} />
    </IconButton>
   </Stack>
  </Paper>
 );
};

export default WebsiteRecap;
